import { useContext, useEffect, useMemo } from "react";

import Color from "color";

import { useMe } from "../hooks/useMe";
import { colorMap } from "./constants";
import { SizeContext } from "./sizeContext";
import type { UseThemeProps } from "./types";
import { isDark, loadGoogleFont } from "./utils";

export const useTheme = (props?: UseThemeProps) => {
  const sizes = useContext(SizeContext);
  if (!sizes) {
    throw new Error("useTheme must be used within a SizeProvider");
  }

  const me = useMe();
  const primaryColour =
    props?.organisation?.primaryColour ||
    me?.ownerOrganisation?.primaryColour ||
    "#14435B";
  const secondaryColour =
    props?.organisation?.secondaryColour ||
    me?.ownerOrganisation?.secondaryColour ||
    "#1b678e";
  const fontFamily = useMemo(
    () =>
      props?.organisation?.fontFamily ||
      me?.ownerOrganisation?.fontFamily || {
        h1: "Inter",
        h2: "Inter",
        h3: "Inter",
        h4: "Inter",
        h5: "Inter",
        body: "Inter",
      },
    [props?.organisation?.fontFamily, me?.ownerOrganisation?.fontFamily],
  );

  useEffect(() => {
    // For each unique font family, load the Google Font
    const fontFamilies = [
      fontFamily.h1,
      fontFamily.h2,
      fontFamily.h3,
      fontFamily.h4,
      fontFamily.h5,
      fontFamily.body,
    ];
    // @ts-ignore -- Set is iterable
    const uniqueFontFamilies = [...new Set(fontFamilies)];
    const validFontFamilies = uniqueFontFamilies.filter(Boolean);
    validFontFamilies.forEach((family) => {
      loadGoogleFont(family, [400, 500, 600, 700]);
    });
  }, [fontFamily]);

  const fullTheme = useMemo(() => {
    const accent = Color(primaryColour).lighten(0.2);
    const secondaryAccent = Color(secondaryColour).lighten(0.6).fade(0.8);

    return {
      sizes,
      primaryColour,
      secondaryColour,
      accent: accent.hex(),
      secondaryAccent: secondaryColour ? secondaryAccent.hex() : accent.hex(),
      shouldBeWhiteOnSecondary: isDark(Color(secondaryColour)),
      shouldBeWhiteOnPrimary: isDark(Color(primaryColour)),
      shouldBeWhiteOnAccent: isDark(accent),
      shouldBeWhiteOnSecondaryAccent: isDark(secondaryAccent),
      boxShadow: "0.5px 0.5px 2.5px 1px #C0C0C0",
      colorMap,
      fontFamily,
    };
  }, [sizes, primaryColour, secondaryColour, fontFamily]);

  return fullTheme;
};
